/**
 * `EXTENSION_SETTINGS`: the definition of every `ExtensionSettingKey`, with the projection WorkBuddy serves to a run. Read by the
 * API's run settings route (the projection), the workflow validator (known keys and fields) and `workflowScopeAt` (the Settings
 * group of the binding picker). A projection returns only the fields its definition declares, so a run never sees the rest of
 * the stored settings document.
 */
import { MetadataEntityFieldType } from '../../metadata/entity/decorators/model/metadata.entity.field.types';
import { ExtensionSettingDefinition, ExtensionSettingKey } from './extension.setting';

/** Every setting a run may read, by key; the `Record` key type makes a key without a definition a compile error. */
export const EXTENSION_SETTINGS: Record<ExtensionSettingKey, ExtensionSettingDefinition> = {
    [ExtensionSettingKey.TimeZone]: {
        title: 'Time zone',
        metadata: {
            displayName: 'Time zone',
            type: MetadataEntityFieldType.String,
        },
        fields: [],
        project: general => general.region?.timezone ?? null,
    },
    [ExtensionSettingKey.BusinessHours]: {
        title: 'Business hours',
        metadata: {
            displayName: 'Business hours',
            type: MetadataEntityFieldType.Custom,
        },
        fields: [
            { path: 'dayStartTime', metadata: { displayName: 'Day starts', type: MetadataEntityFieldType.String } },
            { path: 'dayEndTime', metadata: { displayName: 'Day ends', type: MetadataEntityFieldType.String } },
            { path: 'workWeek', metadata: { displayName: 'Working days', type: MetadataEntityFieldType.String, arrayOf: MetadataEntityFieldType.String } },
        ],
        project: general => ({
            dayStartTime: general.schedule?.dayStartTime ?? null,
            dayEndTime: general.schedule?.dayEndTime ?? null,
            workWeek: [...(general.schedule?.workWeek ?? [])],
        }),
    },
};

/** Whether `key` is a setting a run may read; the run settings route answers 404 otherwise. */
export function isExtensionSettingKey(key: unknown): key is ExtensionSettingKey {

    return Object.values(ExtensionSettingKey).includes(key as ExtensionSettingKey);
}
